import { useEffect, useState } from 'react';
import { changeMemberRole, setMemberRoles, TeamMember, TeamRole } from '../../../api/teams';
import {
  assignInstrument,
  listMinistryInstruments,
  setPrimaryInstrument,
  unassignInstrument,
} from '../../../api/instruments';
import type { Instrument, MemberInstrument } from '../../../types/instrument';
import UserAvatar from '../../../components/feature/UserAvatar';

type Role = 'ADMIN' | 'LEADER' | 'EDITOR' | 'MEMBER';

const roleLabels: Record<Role, { label: string; color: string; description: string }> = {
  ADMIN: { label: 'Admin', color: '#C8A96E', description: 'Controle total da equipe' },
  LEADER: { label: 'Líder', color: '#7EB8A4', description: 'Edita músicas, cria sessões, gerencia eventos' },
  EDITOR: { label: 'Editor', color: '#7E9EC8', description: 'Edita músicas e tons' },
  MEMBER: { label: 'Membro', color: '#8E8E8E', description: 'Visualização, sugestões e votação' },
};

interface Props {
  teamId: string;
  ministryId: string;
  members: TeamMember[];
  teamRoles: TeamRole[];
  memberInstruments: Record<string, MemberInstrument[]>;
  isAdmin: boolean;
  onMembersChange: () => void;
  onMemberInstrumentsChange: () => Promise<void>;
}

export default function TeamMembersView({
  teamId,
  ministryId,
  members,
  teamRoles,
  memberInstruments,
  isAdmin,
  onMembersChange,
  onMemberInstrumentsChange,
}: Props) {
  const [roleDropdown, setRoleDropdown] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');
  const [catalogInstruments, setCatalogInstruments] = useState<Instrument[]>([]);
  const [instrumentActionMember, setInstrumentActionMember] = useState<string | null>(null);
  const [roleAssignmentMember, setRoleAssignmentMember] = useState<string | null>(null);
  const [roleActionMember, setRoleActionMember] = useState<string | null>(null);

  useEffect(() => {
    listMinistryInstruments(ministryId)
      .then(setCatalogInstruments)
      .catch(() => setCatalogInstruments([]));
  }, [ministryId]);

  const filtered = members.filter(
    (m) =>
      m.user_name.toLowerCase().includes(search.toLowerCase()) ||
      m.user_email.toLowerCase().includes(search.toLowerCase())
  );

  const handleRoleChange = async (memberId: string, newRole: Role) => {
    try {
      await changeMemberRole(teamId, memberId, newRole);
      setRoleDropdown(null);
      const member = members.find((m) => m.user_id === memberId);
      const roleInfo = roleLabels[newRole];
      setToast(`Função de ${member?.user_name} atualizada: ${roleInfo.label}`);
      setTimeout(() => setToast(''), 2500);
      onMembersChange();
    } catch {
      setToast('Erro ao alterar função do membro');
      setTimeout(() => setToast(''), 2500);
    }
  };

  const handleInstrumentChange = async (memberId: string, instrumentId: string) => {
    if (!instrumentId) return;
    setInstrumentActionMember(memberId);
    try {
      await assignInstrument(ministryId, memberId, instrumentId);
      await onMemberInstrumentsChange();
      setToast('Instrumento associado ao membro');
    } catch {
      setToast('Erro ao associar instrumento');
    } finally {
      setInstrumentActionMember(null);
      setTimeout(() => setToast(''), 2500);
    }
  };

  const handleInstrumentRemoval = async (memberId: string, instrumentId: string) => {
    setInstrumentActionMember(memberId);
    try {
      await unassignInstrument(ministryId, memberId, instrumentId);
      await onMemberInstrumentsChange();
      setToast('Instrumento removido do membro');
    } catch {
      setToast('Erro ao remover instrumento');
    } finally {
      setInstrumentActionMember(null);
      setTimeout(() => setToast(''), 2500);
    }
  };

  const handlePrimaryInstrument = async (memberId: string, instrumentId: string) => {
    setInstrumentActionMember(memberId);
    try {
      await setPrimaryInstrument(ministryId, memberId, instrumentId);
      await onMemberInstrumentsChange();
      setToast('Instrumento principal atualizado');
    } catch {
      setToast('Erro ao atualizar instrumento principal');
    } finally {
      setInstrumentActionMember(null);
      setTimeout(() => setToast(''), 2500);
    }
  };

  const handleAssignedRoleToggle = async (member: TeamMember, roleId: string) => {
    const assignedRoleIds = (member.assigned_roles ?? []).map((role) => role.id);
    const nextRoleIds = assignedRoleIds.includes(roleId)
      ? assignedRoleIds.filter((id) => id !== roleId)
      : [...assignedRoleIds, roleId];
    setRoleActionMember(member.user_id);
    try {
      await setMemberRoles(teamId, member.user_id, nextRoleIds);
      await onMembersChange();
      setToast('Papéis do membro atualizados');
    } catch {
      setToast('Erro ao atualizar papéis do membro');
    } finally {
      setRoleActionMember(null);
      setTimeout(() => setToast(''), 2500);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-white/5 bg-[#0D0C09]">
        <div>
          <h1
            className="text-white font-semibold text-lg"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Membros da Equipe
          </h1>
          <p className="text-white/30 text-xs mt-0.5">{members.length} membros</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-white/30 text-sm"></i>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar membro..."
              className="w-48 bg-white/5 border border-white/5 rounded-lg pl-9 pr-4 py-2 text-white text-sm placeholder-white/20 outline-none focus:border-white/20 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Members List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.map((member) => {
          const assignedInstruments = memberInstruments[member.user_id] ?? [];
          const assignedInstrumentIds = new Set(assignedInstruments.map((instrument) => instrument.instrument_id));
          const assignedRoles = member.assigned_roles ?? [];
          return (
            <div
              key={member.user_id}
              className="flex flex-wrap items-center gap-4 px-6 md:px-8 py-4 border-b border-white/5 hover:bg-white/[0.01] transition-colors"
            >
              {/* Avatar */}
              <div className="flex-shrink-0">
                <UserAvatar
                  name={member.user_name}
                  avatarUrl={member.avatar_url}
                  size="sm"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{member.user_name}</p>
                <p className="text-white/30 text-xs truncate">{member.user_email}</p>
              </div>

              {/* Instruments (emoji chips) */}
              <div className="flex flex-wrap items-center gap-1.5 w-full md:w-auto md:flex-1">
                {assignedInstruments.map((inst) => (
                  <span
                    key={inst.id}
                    className={`inline-flex items-center gap-1 text-xs pl-2 pr-1 py-1 rounded ${inst.is_primary ? 'bg-[#C8A96E]/10 text-[#C8A96E]' : 'bg-white/5 text-white/70'}`}
                  >
                    {inst.instrument_name}
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          title={inst.is_primary ? 'Instrumento principal' : 'Definir como principal'}
                          aria-label={`${inst.is_primary ? 'Instrumento principal' : 'Definir como principal'}: ${inst.instrument_name}`}
                          disabled={inst.is_primary || instrumentActionMember === member.user_id}
                          onClick={() => handlePrimaryInstrument(member.user_id, inst.instrument_id)}
                          className="w-5 h-5 flex items-center justify-center rounded hover:bg-white/10 disabled:opacity-60"
                        >
                          <i className={`${inst.is_primary ? 'ri-star-fill' : 'ri-star-line'} text-[11px]`} />
                        </button>
                        <button
                          type="button"
                          title={`Remover ${inst.instrument_name}`}
                          aria-label={`Remover ${inst.instrument_name}`}
                          disabled={instrumentActionMember === member.user_id}
                          onClick={() => handleInstrumentRemoval(member.user_id, inst.instrument_id)}
                          className="w-5 h-5 flex items-center justify-center rounded hover:bg-white/10 disabled:opacity-40"
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      </>
                    )}
                  </span>
                ))}
                {isAdmin && (
                  <select
                    aria-label={`Adicionar instrumento para ${member.user_name}`}
                    value=""
                    disabled={instrumentActionMember === member.user_id}
                    onChange={(event) => handleInstrumentChange(member.user_id, event.target.value)}
                    className="max-w-full bg-white/5 border border-white/5 rounded px-2 py-1 text-white/60 text-xs outline-none focus:border-white/20 disabled:opacity-40"
                  >
                    <option value="" className="bg-[#1A1915]">+ Instrumento</option>
                    {catalogInstruments
                      .filter((instrument) => !assignedInstrumentIds.has(instrument.id))
                      .map((instrument) => (
                        <option key={instrument.id} value={instrument.id} className="bg-[#1A1915]">
                          {instrument.name}
                        </option>
                      ))}
                  </select>
                )}
              </div>

              {/* Assigned ministry roles */}
              <div className="w-full flex flex-wrap items-center gap-1.5">
                {assignedRoles.length === 0 && !isAdmin && (
                  <span className="text-white/25 text-xs">Sem papéis</span>
                )}
                {assignedRoles.map((role) => (
                  <span key={role.id} className="inline-flex items-center gap-1 bg-white/5 text-white/70 text-xs px-2 py-1 rounded">
                    <span>{role.icon}</span>
                    {role.name}
                  </span>
                ))}
                {isAdmin && (
                  <button
                    type="button"
                    aria-expanded={roleAssignmentMember === member.user_id}
                    onClick={() => setRoleAssignmentMember(
                      roleAssignmentMember === member.user_id ? null : member.user_id
                    )}
                    className="text-[#C8A96E] hover:text-[#D4B87A] text-xs px-2 py-1 rounded border border-[#C8A96E]/20"
                  >
                    <i className="ri-user-settings-line mr-1" />
                    Papéis ({assignedRoles.length})
                  </button>
                )}
                {isAdmin && roleAssignmentMember === member.user_id && (
                  <div className="w-full max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1 bg-[#15140F] border border-white/10 rounded-lg p-2">
                    {teamRoles.map((role) => (
                      <label key={role.id} className="flex items-center gap-2 px-2 py-2 text-xs text-white/70 hover:bg-white/5 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={assignedRoles.some((assignedRole) => assignedRole.id === role.id)}
                          disabled={roleActionMember === member.user_id}
                          onChange={() => handleAssignedRoleToggle(member, role.id)}
                          className="accent-[#C8A96E]"
                        />
                        <span>{role.icon}</span>
                        <span className="truncate">{role.name}</span>
                      </label>
                    ))}
                    {teamRoles.length === 0 && (
                      <p className="text-white/30 text-xs px-2 py-2">Nenhuma função disponível.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Role Badge */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => isAdmin && setRoleDropdown(roleDropdown === member.user_id ? null : member.user_id)}
                  aria-label={`Função: ${roleLabels[member.role as Role].label}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-opacity ${isAdmin ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                  style={{
                    backgroundColor: roleLabels[member.role as Role].color + '20',
                    color: roleLabels[member.role as Role].color,
                  }}
                >
                  Função: {roleLabels[member.role as Role].label}
                </button>

                {/* Role Dropdown */}
                {roleDropdown === member.user_id && (
                  <div className="absolute left-0 md:left-auto md:right-0 top-10 bg-[#1A1915] border border-white/10 rounded-xl overflow-hidden z-30 w-56 max-w-[calc(100vw-3rem)] shadow-lg">
                    <p className="px-4 py-3 border-b border-white/5 text-white/40 text-xs uppercase tracking-wider">
                      Função de acesso
                    </p>
                    {(Object.keys(roleLabels) as Role[]).map((role) => {
                      const info = roleLabels[role];
                      const isCurrent = member.role === role;
                      return (
                        <button
                          key={role}
                          onClick={() => handleRoleChange(member.user_id, role)}
                          className={`w-full text-left px-4 py-3 cursor-pointer transition-colors ${
                            isCurrent ? 'bg-white/[0.02]' : 'hover:bg-white/[0.03]'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-medium" style={{ color: info.color }}>
                              {info.label}
                            </span>
                            {isCurrent && <i className="ri-check-line text-[#C8A96E] text-xs"></i>}
                          </div>
                          <p className="text-white/30 text-xs">{info.description}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-[#1A1915] border border-white/10 rounded-xl px-5 py-3 text-white text-sm z-50 animate-pulse">
          <i className="ri-check-line text-[#7EB8A4] mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}
